/**
 * Step 2: Prompt Configuration
 *
 * Select which properties from the dataset to use as quiz prompts.
 * Example: For US States, you might select "Name", "Capital", "Abbreviation"
 */

import { useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  useStudioStore,
  selectDataset,
  selectPromptKeys,
  setPromptKeys,
} from "@/stores/useStudioStore";
import { countUniquePrompts } from "@/utils/promptGrouping";

export function Step2PromptConfiguration() {
  const dataset = useStudioStore(selectDataset);
  const selectedKeys = useStudioStore(selectPromptKeys);

  // Extract all unique property keys from the dataset (preserving order from first feature)
  const availableProperties = useMemo(() => {
    if (!dataset.collection || dataset.collection.features.length === 0) return [];

    // Get keys from first feature to preserve order
    const firstFeature = dataset.collection.features[0];
    if (!firstFeature?.properties) return [];

    const orderedKeys = Object.keys(firstFeature.properties);

    // Add any additional keys from other features (if they exist)
    const allKeys = new Set(orderedKeys);
    dataset.collection.features.forEach((feature) => {
      if (feature.properties) {
        Object.keys(feature.properties).forEach((key) => {
          if (!allKeys.has(key)) {
            orderedKeys.push(key);
            allKeys.add(key);
          }
        });
      }
    });

    return orderedKeys;
  }, [dataset.collection]);

  // Get sample values for each property (first non-empty value)
  const propertySamples = useMemo(() => {
    if (!dataset.collection) return new Map<string, string>();

    const samples = new Map<string, string>();

    availableProperties.forEach((key) => {
      for (const feature of dataset.collection!.features) {
        const value = feature.properties?.[key];
        if (value && String(value).trim()) {
          samples.set(key, String(value));
          break;
        }
      }
    });

    return samples;
  }, [dataset.collection, availableProperties]);

  // Auto-select first property on mount (or all if only one property)
  useEffect(() => {
    if (availableProperties.length === 0) return;
    if (selectedKeys.length > 0) return; // Already has selections

    const firstProperty = availableProperties[0];
    if (firstProperty) {
      setPromptKeys([firstProperty]);
    }
  }, [availableProperties, selectedKeys.length]);

  // Toggle property selection
  const handleToggle = (key: string, checked: boolean) => {
    if (checked) {
      setPromptKeys([...selectedKeys, key]);
    } else {
      // Don't allow deselecting the last key
      if (selectedKeys.length === 1) {
        return;
      }
      setPromptKeys(selectedKeys.filter((k) => k !== key));
    }
  };

  if (!dataset.collection) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <p>No dataset loaded. Please go back to Step 1.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configure Prompts</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Select which properties to quiz on. You need at least one property.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Dataset Info */}
        <div className="p-4 rounded-lg bg-muted/50 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Dataset:</span>
            <span className="font-medium">{dataset.label}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Features:</span>
            <span className="font-medium">{dataset.collection.features.length}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Properties available:</span>
            <span className="font-medium">{availableProperties.length}</span>
          </div>
        </div>

        {/* Property Selection */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Available Properties</h3>

          {availableProperties.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No properties found in this dataset
            </p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {availableProperties.map((key) => {
                const isSelected = selectedKeys.includes(key);
                const sample = propertySamples.get(key);

                return (
                  <label
                    key={key}
                    className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer transition-colors"
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) => handleToggle(key, checked as boolean)}
                      disabled={isSelected && selectedKeys.length === 1}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{key}</span>
                        {isSelected && (
                          <Badge variant="secondary" className="text-xs">
                            Selected
                          </Badge>
                        )}
                      </div>
                      {sample && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Example: "{sample}"
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

      </CardContent>
    </Card>
  );
}
